import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { Textarea } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import type { EmpiConflictDetail } from "@/shared/types/empi";
import { COMMENT_MAX_LENGTH, type ReviewAnswer } from "@/portals/patient/api/reviewItemsApi";
import { ConflictOptions } from "@/features/empi/ConflictOptions";
import { RESOURCE_NOUN, conflictReasonText, optionName } from "@/features/empi/empiPresentation";
import { ACTION_LABELS } from "@/features/patient/human-review/reviewActionLabels";

/**
 * GOLDEN_RECORD_CONFLICT (Module 5): the patient's records of one condition,
 * medicine, allergy or test result disagree. The patient may point at the
 * one they believe is right (USE_THIS_VALUE + chosenRecordId), say they're
 * not sure, skip, or just send a message — whatever the item's allowedAnswers
 * offer. Advisory only: a clinician makes the decision, and the copy says so.
 */

export const CONFLICT_MESSAGES = {
  sent: "Thank you. Your clinician will make the final decision.",
  advisory:
    "Your clinician makes the final decision. Picking one helps them decide, but it doesn't change your record by itself.",
};

const BRAND_BUTTON =
  "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";

export function ConflictPicker({
  conflict,
  allowedAnswers,
  canSkip,
  inFlight,
  onSubmit,
}: {
  conflict: EmpiConflictDetail;
  allowedAnswers: ReviewAnswer[];
  /** The item is still in the patient's own window (Skip exists only there). */
  canSkip: boolean;
  /** The answer being sent, if any. */
  inFlight: ReviewAnswer | null;
  onSubmit: (answer: ReviewAnswer, extra: { chosenRecordId?: string; comment?: string }) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [notSure, setNotSure] = useState(false);
  const [comment, setComment] = useState("");
  const allows = (a: ReviewAnswer) => allowedAnswers.includes(a);
  const name = optionName(conflict.resourceType, conflict.options[0]?.fields ?? {});
  const noun = RESOURCE_NOUN[conflict.resourceType];
  const sending = inFlight !== null;
  const trimmed = comment.trim();

  // What Send sends: a pick, else "not sure", else the message on its own.
  const answer: ReviewAnswer | null = chosen
    ? "USE_THIS_VALUE"
    : notSure
      ? "NOT_SURE"
      : trimmed && allows("REPLY")
        ? "REPLY"
        : null;

  return (
    <div className="grid gap-6">
      <section className="rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4" aria-label="Why we're asking">
        <p className="text-sm font-semibold text-[#0A2947]">Your records don't agree about this {noun}: {name}.</p>
        <p className="mt-1 text-sm text-[#374151]" data-testid="conflict-reason">{conflictReasonText(conflict.conflictReason, noun)}</p>
        <p className="mt-1 flex items-start gap-1.5 text-sm text-[#4B5563]" data-testid="conflict-advisory">
          <Icon icon="lucide:stethoscope" className="mt-0.5 h-4 w-4 shrink-0 text-[#0A2947]" />
          {CONFLICT_MESSAGES.advisory}
        </p>
      </section>

      <section>
        <p className="mb-3 text-sm font-extrabold text-[#0A2947]">
          {allows("USE_THIS_VALUE") ? "Pick the one you think is right" : "What your records say"}
        </p>
        <ConflictOptions
          detail={conflict}
          label="Your records"
          selectedId={chosen}
          onSelect={allows("USE_THIS_VALUE") ? (id) => { setChosen(chosen === id ? null : id); setNotSure(false); } : undefined}
        />
        {allows("NOT_SURE") && (
          <Button
            variant="outline"
            role="checkbox"
            aria-checked={notSure}
            onClick={() => { setNotSure(!notSure); setChosen(null); }}
            className={cn(
              "!mt-3 !flex !h-auto !w-full !items-start !justify-start !gap-2 !whitespace-normal !rounded-2xl !px-4 !py-3 !text-left !text-sm !font-semibold !text-[#0A2947]",
              notSure ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!bg-[#FFF2F4]" : "!border-[#DDE1E8] !bg-white hover:!bg-white",
            )}
          >
            <Icon icon={notSure ? "lucide:circle-dot" : "lucide:circle"} className={cn("mt-0.5 h-4 w-4 shrink-0", notSure ? "text-[#E4002B]" : "text-[#9CA3AF]")} />
            <span>
              I'm not sure
              <span className="block text-xs font-normal text-[#6B7280]">Your clinician decides without a pick from you.</span>
            </span>
          </Button>
        )}
      </section>

      {allows("REPLY") && (
        <section>
          <label htmlFor="conflict-comment" className="mb-2 block text-sm font-semibold text-[#0A2947]">Message to your clinician</label>
          <Textarea
            id="conflict-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
            maxLength={COMMENT_MAX_LENGTH}
            showCount
            rows={3}
            placeholder="Anything your clinician should know"
            className="!rounded-lg"
          />
          <p className="!mb-0 mt-1.5 text-xs text-[#9CA3AF]">Choose an answer, write a message, or both.</p>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {canSkip && allows("SKIPPED") && (
          <Button
            variant="ghost"
            disabled={sending && inFlight !== "SKIPPED"}
            loading={inFlight === "SKIPPED"}
            onClick={() => onSubmit("SKIPPED", {})}
            className="!h-10 !rounded-lg !px-4 !text-sm !font-semibold !text-[#4B5563] hover:!bg-[#F3F4F6] hover:!text-[#E4002B]"
          >
            {ACTION_LABELS.skip}
          </Button>
        )}
        <Button
          disabled={!answer || inFlight === "SKIPPED"}
          loading={sending && inFlight !== "SKIPPED"}
          onClick={() => answer && onSubmit(answer, { ...(chosen ? { chosenRecordId: chosen } : {}), ...(trimmed ? { comment: trimmed } : {}) })}
          className={BRAND_BUTTON}
        >
          {ACTION_LABELS.send}
        </Button>
      </div>
    </div>
  );
}
